import { DateRange } from '../../shared/components/date-range-picker/date-range-picker.component';
import {
  formatCalendarDateInTimeZone,
  getUserTimezone,
} from '../../shared/utils/date.util';

export function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Calendar `YYYY-MM-DD` for `date` in the employee/shop location timezone. */
function formatShopCalendarDate(date: Date): string {
  return formatCalendarDateInTimeZone(date, getUserTimezone()) || formatDateForApi(date);
}

/**
 * Anchor at UTC noon on the shop's calendar day so week/month math uses the
 * location day, not the browser's local day.
 */
function getShopCalendarAnchor(date: Date = new Date()): Date {
  const ymd = formatShopCalendarDate(date);
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

function formatUtcYmd(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getStartOfWeek(date: Date): Date {
  const startOfWeek = new Date(date);
  const day = startOfWeek.getUTCDay();
  startOfWeek.setUTCDate(startOfWeek.getUTCDate() - day);
  return startOfWeek;
}

function getEndOfWeek(date: Date): Date {
  const endOfWeek = getStartOfWeek(date);
  endOfWeek.setUTCDate(endOfWeek.getUTCDate() + 6);
  return endOfWeek;
}

function getStartOfBiWeek(date: Date): Date {
  const startOfWeek = getStartOfWeek(date);
  const startOfYearWeek = getStartOfWeek(new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 12)));
  const weekOffset = Math.round(
    (startOfWeek.getTime() - startOfYearWeek.getTime()) / (24 * 60 * 60 * 1000 * 7)
  );
  const biWeekStartOffset = weekOffset - (weekOffset % 2);
  const startOfBiWeek = new Date(startOfYearWeek);
  startOfBiWeek.setUTCDate(startOfYearWeek.getUTCDate() + biWeekStartOffset * 7);
  return startOfBiWeek;
}

function getEndOfBiWeek(date: Date): Date {
  const endOfBiWeek = getStartOfBiWeek(date);
  endOfBiWeek.setUTCDate(endOfBiWeek.getUTCDate() + 13);
  return endOfBiWeek;
}

function getStartOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12));
}

function getEndOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 12));
}

function getStartOfYear(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 12));
}

function getEndOfYear(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), 11, 31, 12));
}

/**
 * Maps a period label to ISO date range query params, or null for All Time.
 * Relative periods (Today, This Week, …) use the employee/shop location timezone.
 */
export function getDateRangeForPeriod(
  period: string,
  customDateRange: DateRange
): { start: string; end: string } | null {
  const shopNow = getShopCalendarAnchor();

  switch (period) {
    case 'Today': {
      const today = formatShopCalendarDate(new Date());
      return { start: today, end: today };
    }
    case 'This Week':
      return {
        start: formatUtcYmd(getStartOfWeek(shopNow)),
        end: formatUtcYmd(getEndOfWeek(shopNow)),
      };
    case 'This Bi-Week':
      return {
        start: formatUtcYmd(getStartOfBiWeek(shopNow)),
        end: formatUtcYmd(getEndOfBiWeek(shopNow)),
      };
    case 'This Month':
      return {
        start: formatUtcYmd(getStartOfMonth(shopNow)),
        end: formatUtcYmd(getEndOfMonth(shopNow)),
      };
    case 'Last 6 Months': {
      const end = formatShopCalendarDate(new Date());
      const startAnchor = new Date(shopNow);
      startAnchor.setUTCMonth(startAnchor.getUTCMonth() - 6);
      return { start: formatUtcYmd(startAnchor), end };
    }
    case 'This Year':
      return {
        start: formatUtcYmd(getStartOfYear(shopNow)),
        end: formatUtcYmd(getEndOfYear(shopNow)),
      };
    case 'Custom':
      if (customDateRange.startDate && customDateRange.endDate) {
        return {
          start: formatDateForApi(customDateRange.startDate),
          end: formatDateForApi(customDateRange.endDate),
        };
      }
      return null;
    case 'All Time':
    default:
      return null;
  }
}
